import React from 'react';
import { arrayOf, bool, func, node, oneOfType, shape, string } from 'prop-types';
import { MenuItem, Select as MuiSelect, Typography } from '@mui/material';

import SelectIcon from '../SelectIcon';

const selectMenuProps = theme => ({
  '.MuiPaper-root.MuiPopover-paper': {
    borderRadius: theme.border.radius,
    borderTopLeftRadius: '0',
    borderTopRightRadius: '0',
    border: `1px solid ${theme.palette.primary.main}`,
    marginTop: '-6px', // sliding upward to cover the select button's bottom rounded corners
    boxSizing: 'border-box',

    ' .MuiList-root': {
      padding: '0px',

      ' .MuiMenuItem-root': {
        padding: '6px',
        borderRadius: '0',
        color: theme.palette.text.secondary,
        whiteSpace: 'normal',
        overflowWrap: 'anywhere',
      },
    },
  },
  '.MuiMenu-paper': {
    '&::-webkit-scrollbar': {
      width: '6px',
    },
    '&::-webkit-scrollbar-thumb': {
      backgroundColor: theme.palette.grey[400],
      borderRadius: '10px',
    },
  },
});

/**
 * @param {array} options - optional, otherwise `children` is required
 * @param {node} children - optional, otherwise `options` is required
 * @param {object} selectStyles - optional styles for the select component
 * (ie: width of the select button)
 * @param {object} menuProps = optional, additional style props passed to the
 * MenuProps sx object (eg: limited dropdown height)
 * @param {func} renderValue - optional modified rendered value,
 * otherwise `value` is used
 * @param {string} placeholderOptionLabel - optional label for the placeholder
 * option that will have a null value
 * @param {bool} multiple - optional, if true, allows multiple selections
 */
const Select = ({
  id,
  name,
  value,
  label,
  labelId,
  options,
  children,
  onChange,
  onBlur,
  selectStyles,
  menuProps,
  placeholderOptionLabel,
  disabled,
  required,
  renderValue,
  typeVariant, // clean up `typeVariant` after redesign
  multiple,
  error,
}) => {
  return (
    <MuiSelect
      id={id}
      name={name}
      onChange={onChange}
      onBlur={onBlur}
      value={value}
      renderValue={renderValue}
      label={label}
      labelId={labelId}
      IconComponent={disabled ? null : SelectIcon}
      MenuProps={{
        sx: theme => ({
          ...selectMenuProps(theme),
          ...menuProps,
        }),
      }}
      inputProps={{
        'aria-label': name,
      }}
      sx={selectStyles}
      disabled={disabled}
      displayEmpty={Boolean(placeholderOptionLabel)}
      required={required}
      multiple={multiple}
      error={error}
    >
      {placeholderOptionLabel && (!required || (required && !value)) && (
        <MenuItem value="" disabled={required}>
          <Typography sx={{ textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap' }} variant={typeVariant}>{placeholderOptionLabel}</Typography>
        </MenuItem>
      )}
      {options && options.map(option => (
        <MenuItem key={option.value} value={option.value}>
          {typeof option.label === 'string' ? (
            <Typography sx={{ textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap' }} variant={typeVariant}>{option.label}</Typography>
          ) : (
            option.label
          )}
        </MenuItem>
      ))}
      {children && !options ? children : null}
    </MuiSelect>
  );
};

export default Select;

Select.propTypes = {
  id: string.isRequired,
  name: string.isRequired,
  value: string,
  renderValue: func,
  children: node,
  options: arrayOf(
    shape({
      value: string,
      label: oneOfType([string, node]),
    }),
  ),
  onChange: func.isRequired,
  onBlur: func,
  label: string,
  labelId: string,
  selectStyles: shape({}),
  placeholderOptionLabel: string,
  disabled: bool,
  required: bool,
  typeVariant: string,
  menuProps: shape({}),
  multiple: bool,
  error: bool,
};

Select.defaultProps = {
  value: '',
  label: null,
  labelId: null,
  onBlur: null,
  renderValue: null,
  children: null,
  options: null,
  placeholderOptionLabel: '',
  selectStyles: {
    width: '100%',
  },
  disabled: false,
  required: false,
  typeVariant: 'body2',
  menuProps: {},
  multiple: false,
  error: false,
};
